import { Skeleton } from 'antd';

/**
 * S4-T09 — Brand-shaped skeleton primitives.
 *
 * Replaces bare `<Spin />` indicators with skeletons that mimic the actual
 * content shape, so the loading frame doesn't visibly reflow into the loaded
 * one. Use these whenever a Suspense boundary, lazy import, or async fetch
 * needs a placeholder.
 */

export interface ChartSkeletonProps {
  /** Pixel height of the chart placeholder. Default 280, matches typical AntD Plots. */
  height?: number;
}

export function ChartSkeleton({ height = 280 }: ChartSkeletonProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 8,
      }}
    >
      <Skeleton.Input active size="small" style={{ width: 160, height: 16 }} />
      <Skeleton.Node active style={{ height, width: '100%' }} />
    </div>
  );
}

export interface KpiRowSkeletonProps {
  /** Number of KPI tiles. Default 4. */
  count?: number;
}

export function KpiRowSkeleton({ count = 4 }: KpiRowSkeletonProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${count}, 1fr)`,
        gap: 16,
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton.Node key={i} active style={{ height: 88, width: '100%' }} />
      ))}
    </div>
  );
}

/**
 * Full-page skeleton for the role-dashboard switcher. Matches the typical
 * shape: brand greeting strip → KPI row → 2-up chart row → action card.
 */
export function DashboardSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton.Input active size="large" style={{ width: 280, height: 30 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 16 }} />
      </div>
      <KpiRowSkeleton count={4} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ChartSkeleton height={240} />
        <ChartSkeleton height={240} />
      </div>
      <Skeleton.Node active style={{ height: 160, width: '100%' }} />
    </div>
  );
}
